import { fetchAuth } from '../utils/fetchAuth';

export type ProcessStatusKey =
  | 'extractionError'
  | 'riftError'
  | 'duplicate'
  // In-flight processes are excluded from every listing; this key only reaches
  // the UI on a process detail opened by a direct link.
  | 'processing'
  | 'paid'
  | 'alreadySettled';

export type DashboardSummary = {
  extraction_error: number;
  rift_error: number;
  duplicate: number;
  paid: number;
  already_settled: number;
};

export type DashboardProcess = {
  id: number;
  claim_number: string;
  status: string;
  status_label: string;
  status_key: ProcessStatusKey;
  created_at: string;
  updated_at: string;
};

export type DashboardData = {
  summary: DashboardSummary;
  count: number;
  next_page: number | null;
  previous_page: number | null;
  recent_processes: DashboardProcess[];
};

export type ProcessSummary = {
  id: number;
  email_message_summary: ProcessEmailSummary | null;
  primary_document: number | null;
  primary_document_summary: ProcessDocumentSummary | null;
  status: string;
  status_key: ProcessStatusKey;
  status_label: string;
  eligibility_status: string;
  eligibility_reason: string;
  case_type: string;
  claim_number: string;
  claim_number_source: string;
  provider_nif: string;
  provider_iban: string;
  invoice_number: string;
  total_amount: string | null;
  payment_executed: boolean;
  receipt_number: string;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
  document_count: number;
};

export type ProcessEmailSummary = {
  id: number;
  extracted_claim_number: string;
  sender_email: string;
  sender_name?: string;
  subject: string;
  body_preview?: string;
  received_at: string;
  sent_at?: string | null;
  graph_conversation_id?: string;
  graph_web_link?: string;
};

export type ProcessDocumentSummary = {
  id: number;
  original_filename: string;
  document_type: string;
  file_size: number | null;
  file_hash: string;
  extraction_status: string;
  extraction_error: string;
  extracted_fields: Record<string, unknown>;
  missing_required_fields: string[];
  created_at: string;
  download_url?: string | null;
  preview_url?: string | null;
};

export type ProcessIngestionRecordSummary = {
  id: number;
  mailbox: string;
  graph_message_id: string;
  internet_message_id: string;
  source_folder: string;
  destination_folder: string;
  status: string;
  claim_number: string;
  claim_number_source: string;
  case_type: string;
  attachment_count: number;
  supported_attachment_count: number;
  unsupported_attachment_count: number;
  processed_document_count: number;
  eligibility_checks: Record<string, unknown>;
  extracted_fields: Record<string, unknown>;
  error_message: string;
  email_moved: boolean;
  move_suppressed: boolean;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
};

export type ExtractedInvoiceFields = {
  provider_nif: string;
  provider_iban: string;
  invoice_number: string;
  total_amount: string | null;
  // false when the values come from document extraction on a not-eligible process
  // (read but not validated); true when they mirror the validated Process columns.
  validated: boolean;
};

export type ProcessDetail = ProcessSummary & {
  documents: ProcessDocumentSummary[];
  ingestion_records: ProcessIngestionRecordSummary[];
  latest_ingestion_record: ProcessIngestionRecordSummary | null;
  failure_reason: string;
  paid_but_not_archived: boolean;
  extracted_invoice_fields: ExtractedInvoiceFields;
};

export type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export type ProcessQueryParams = {
  search?: string;
  id?: string;
  status?: string;
  eligibility_status?: string;
  case_type?: string;
  date_range?: string;
  page?: number;
  page_size?: number;
};

const defaultDashboardData: DashboardData = {
  summary: {
    extraction_error: 0,
    rift_error: 0,
    duplicate: 0,
    paid: 0,
    already_settled: 0,
  },
  count: 0,
  next_page: null,
  previous_page: null,
  recent_processes: [],
};

function buildQueryString(params: ProcessQueryParams = {}): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      query.set(key, String(value));
    }
  });
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
}

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = 'Falha ao carregar dados.';
    try {
      const data = await response.json();
      message = data?.detail || data?.message || message;
    } catch {
      // Keep the generic message if the backend does not return JSON.
    }
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

export async function getDashboardData(
  params: ProcessQueryParams = {},
  signal?: AbortSignal,
): Promise<DashboardData> {
  const response = await fetchAuth(`processes/dashboard/${buildQueryString(params)}`, { signal });
  const data = await parseJson<Partial<DashboardData>>(response);
  return {
    summary: { ...defaultDashboardData.summary, ...data.summary },
    count: data.count ?? 0,
    next_page: data.next_page ?? null,
    previous_page: data.previous_page ?? null,
    recent_processes: data.recent_processes || [],
  };
}

export type PipelineRunStatus = 'running' | 'success' | 'failed' | 'skipped';

export type PipelineLastRun = {
  started_at: string;
  finished_at: string | null;
  status: PipelineRunStatus;
  trigger: 'schedule' | 'manual';
  /** Still `running` after the pipeline lock expired: its worker died mid-run. */
  stalled: boolean;
};

export type PipelineHealth = {
  count: number;
  last_run: PipelineLastRun | null;
  /** When the pipeline last finished handling an invoice; null if it never has. */
  last_processed_at: string | null;
};

/**
 * Pipeline health for the navbar: work in flight, the last run and when an
 * invoice was last handled. Never filtered — it is global, unlike every other
 * endpoint here.
 */
export async function getInFlightCount(signal?: AbortSignal): Promise<PipelineHealth> {
  const response = await fetchAuth('processes/in_flight/', { signal });
  const data = await parseJson<Partial<PipelineHealth>>(response);
  return {
    count: data.count ?? 0,
    last_run: data.last_run ? { ...data.last_run, stalled: data.last_run.stalled ?? false } : null,
    last_processed_at: data.last_processed_at ?? null,
  };
}

export async function getProcesses(
  params: ProcessQueryParams = {},
  signal?: AbortSignal,
): Promise<PaginatedResponse<ProcessSummary>> {
  const response = await fetchAuth(`processes/${buildQueryString(params)}`, { signal });
  return parseJson<PaginatedResponse<ProcessSummary>>(response);
}

export async function getProcessDetail(
  processId: string | number,
  signal?: AbortSignal,
): Promise<ProcessDetail> {
  const response = await fetchAuth(`processes/${processId}/`, { signal });
  return parseJson<ProcessDetail>(response);
}
